import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "@/lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { User, KeyRound, Bell, Shield, AlertTriangle, Mail } from "lucide-react";

function Section({ icon: Icon, title, desc, children }) {
  return (
    <div className="border border-border rounded-2xl p-6 bg-card">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-primary/15 to-violet-500/15 flex items-center justify-center ring-1 ring-border">
          <Icon className="h-4 w-4 text-primary" />
        </div>
        <div>
          <h3 className="font-bold tracking-tight">{title}</h3>
          {desc && <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

export default function Settings() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "" });
  const [savingPwd, setSavingPwd] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [emailNotifs, setEmailNotifs] = useState({ weekly: true, alerts: true, product: false });

  const saveName = async (e) => {
    e.preventDefault(); setSavingName(true);
    try {
      const r = await apiClient.put("/account", { name });
      setUser(r.data); toast.success("Nom mis à jour");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSavingName(false); }
  };

  const savePwd = async (e) => {
    e.preventDefault();
    if (pwd.next.length < 6) { toast.error("Minimum 6 caractères"); return; }
    setSavingPwd(true);
    try {
      await apiClient.put("/account", { current_password: pwd.current, new_password: pwd.next });
      toast.success("Mot de passe mis à jour"); setPwd({ current: "", next: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSavingPwd(false); }
  };

  const deleteAccount = async () => {
    if (!confirm("Supprimer définitivement votre compte et toutes vos données ? Cette action est irréversible.")) return;
    if (!confirm("Confirmation finale : toutes vos données seront supprimées dans les 24 heures.")) return;
    setDeleting(true);
    try {
      await apiClient.delete("/account");
      toast.success("Compte supprimé");
      await logout(); nav("/");
    } catch (err) { toast.error("Erreur"); setDeleting(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-primary">Compte</p>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter mt-2 font-display">
          Paramètres du <span className="font-serif-italic font-normal">compte</span>
        </h1>
        <p className="text-sm text-muted-foreground mt-2">Gérez vos informations, votre sécurité et vos préférences.</p>
      </div>

      <Section icon={User} title="Identité" desc="Apparaît dans votre tableau de bord et vos exports.">
        <form onSubmit={saveName} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold">Nom complet</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-md h-11" data-testid="settings-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold">Email</Label>
            <Input value={user?.email || ""} disabled className="rounded-md h-11 opacity-60" />
            <p className="text-[10px] text-muted-foreground">L'email ne peut être modifié.</p>
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <Button disabled={savingName} className="rounded-full" data-testid="settings-save-name-btn">
              {savingName ? "…" : "Enregistrer"}
            </Button>
          </div>
        </form>
      </Section>

      <Section icon={KeyRound} title="Sécurité" desc="Mot de passe et accès à votre compte.">
        <form onSubmit={savePwd} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold">Mot de passe actuel</Label>
            <Input type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} className="rounded-md h-11" data-testid="settings-pwd-current" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold">Nouveau mot de passe</Label>
            <Input type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} className="rounded-md h-11" minLength={6} data-testid="settings-pwd-new" />
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <Button disabled={savingPwd || !pwd.next} className="rounded-full" data-testid="settings-save-pwd-btn">
              {savingPwd ? "…" : "Mettre à jour"}
            </Button>
          </div>
        </form>
      </Section>

      <Section icon={Bell} title="Notifications" desc="Choisissez ce que vous voulez recevoir.">
        <div className="space-y-3">
          {[
            { k: "weekly", t: "Récap hebdomadaire", d: "Chaque dimanche soir, votre semaine en chiffres." },
            { k: "alerts", t: "Alertes business", d: "Avis Google, baisse de score, opportunité de publication." },
            { k: "product", t: "Nouveautés produit", d: "Nouvelles fonctionnalités et bonnes pratiques (1×/mois)." },
          ].map(n => (
            <label key={n.k} className="flex items-start gap-3 p-3 rounded-lg border border-border hover:border-primary/40 cursor-pointer transition-colors" data-testid={`notif-pref-${n.k}`}>
              <input type="checkbox" checked={emailNotifs[n.k]} onChange={(e) => setEmailNotifs({ ...emailNotifs, [n.k]: e.target.checked })}
                className="mt-1 h-4 w-4 rounded accent-primary" />
              <div className="flex-1">
                <p className="text-sm font-semibold">{n.t}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{n.d}</p>
              </div>
            </label>
          ))}
        </div>
      </Section>

      <Section icon={Shield} title="Vos données · RGPD" desc="Téléchargez ou supprimez l'ensemble de vos données.">
        <div className="space-y-3">
          <Button variant="outline" className="rounded-full" onClick={() => toast.info("Export disponible sous 24h. Un email vous sera envoyé.")} data-testid="export-data-btn">
            Télécharger toutes mes données
          </Button>
          <p className="text-xs text-muted-foreground">Vous recevrez un email avec un lien chiffré valable 7 jours.</p>
        </div>
      </Section>

      <div className="border border-rose-500/30 rounded-2xl p-6 bg-rose-500/5">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-9 w-9 rounded-lg bg-rose-500/10 flex items-center justify-center">
            <AlertTriangle className="h-4 w-4 text-rose-500" />
          </div>
          <div>
            <h3 className="font-bold tracking-tight text-rose-500">Zone sensible</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Action irréversible. Toutes vos données seront supprimées.</p>
          </div>
        </div>
        <Button variant="outline" className="rounded-full text-rose-500 border-rose-500/30 hover:bg-rose-500/10" onClick={deleteAccount} disabled={deleting} data-testid="delete-account-btn">
          {deleting ? "Suppression…" : "Supprimer mon compte"}
        </Button>
      </div>

      <div className="text-center text-xs text-muted-foreground pt-4">
        Une question ? <a href="/contact" className="text-primary hover:underline inline-flex items-center gap-1"><Mail className="h-3 w-3" />Contacter le support</a>
      </div>
    </div>
  );
}
